import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Paper, Rating, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";
import LoadError from "../../components/admin/LoadError";

const TABS = ["pending", "approved", "rejected"];

export default function Testimonials() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`admin/testimonials?status=${status}`);
      setRows(res.data?.data || []);
    } catch (err) {
      setRows([]);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const setRowStatus = async (row, next) => {
    try {
      await api.patch(`admin/testimonials/${row.id}`, { status: next });
      toast.success(next === "approved" ? "Approved - now live on the home page" : "Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not update");
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`admin/testimonials/${row.id}`);
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        Dashboard /{" "}
        <Box component="span" sx={{ color: "#3B2A9F", fontWeight: 600 }}>
          Testimonials
        </Box>
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5} mb={0.5}>
        Testimonials
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Reviews users send from the app. Only approved ones, with consent ticked, show on the home page.
      </Typography>

      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t} value={t} label={t[0].toUpperCase() + t.slice(1)} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : loadError ? (
        <LoadError what="testimonials" error={loadError} onRetry={load} />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No {status} testimonials yet.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Paper key={row.id} sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Rating value={row.rating} readOnly size="small" />
                <Typography fontWeight={700}>{row.display_name}</Typography>
                {row.child_year && <Chip size="small" label={row.child_year} />}
                {row.exam_board && <Chip size="small" label={row.exam_board} />}
              </Stack>
              <Typography sx={{ mb: 1.5 }}>{row.quote}</Typography>
              <Stack direction="row" spacing={1}>
                {status !== "approved" && (
                  <Button size="small" variant="contained" onClick={() => setRowStatus(row, "approved")}>
                    Approve
                  </Button>
                )}
                {status !== "rejected" && (
                  <Button size="small" variant="outlined" onClick={() => setRowStatus(row, "rejected")}>
                    {status === "approved" ? "Hide" : "Reject"}
                  </Button>
                )}
                <Button size="small" color="error" onClick={() => remove(row)}>
                  Delete
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
